import { ActionError, Button, StepProgress, WaitPhrase, cn } from '@westhetics/ui'
import { useId, useState, type FormEvent, type ReactNode } from 'react'
import { Link, Navigate, useNavigate } from 'react-router'
import { docNumberError, personNameError } from '@/features/clinic-profile/form'
import { formatDocNumber, normalizeDocNumber } from '@/features/clinic-profile/doc-number'
import { isApiError } from '@/lib/api'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import {
  isAcceptablePassword,
  isEmail,
  isPendingVerification,
  useRequestCode,
  useSignup,
} from './api'
import {
  AuthLayout,
  FieldNote,
  PasswordLabel,
  authInlineLink,
  authInput,
  authLink,
  authNote,
} from './auth-layout'
import { RESEND_AFTER_SECONDS, useSecondsUntil } from './resend-timer'

/**
 * Criar conta (docs/areas/autenticacao.md, "Criar conta"; RN-AUT-26 e docs/api/acesso.md). A
 * clínica cria a própria conta em três passos, no leiaute do login: a pessoa (nome, e-mail, senha),
 * a clínica (nome e documento) e o código que confirma o e-mail. `POST /access/signup` cria tudo e
 * manda o código; `verifyCode` o confere e abre a primeira sessão, que cai na implantação
 * (RN-ADM-80). Quem já tem conta é mandado entrar; quem parou antes do código, a "Entrar com código".
 */

type Step = 'account' | 'clinic' | 'confirm'
const STEPS: Step[] = ['account', 'clinic', 'confirm']
const STEP_LABELS: Record<Step, string> = {
  account: 'Sua conta',
  clinic: 'Sua clínica',
  confirm: 'Confirme o e-mail',
}
const STEP_TITLES: Record<Step, string> = {
  account: 'Criar sua conta',
  clinic: 'Sua clínica',
  confirm: 'Confirme o seu e-mail',
}

type FieldKey = 'name' | 'email' | 'password' | 'clinicName' | 'docNumber'
const ACCOUNT_FIELDS: FieldKey[] = ['name', 'email', 'password']
const CLINIC_FIELDS: FieldKey[] = ['clinicName', 'docNumber']
/** O caminho do 422 da API → o campo da tela. */
const API_FIELDS: Record<string, FieldKey> = {
  name: 'name',
  email: 'email',
  password: 'password',
  'clinic.name': 'clinicName',
  'clinic.docNumber': 'docNumber',
  clinic: 'clinicName',
}

const MESSAGES = {
  email: 'Confira o e-mail: precisa ter @ e um domínio, como nome@clinica.com.br.',
  password: 'A senha precisa de pelo menos 8 caracteres, com letras e números.',
  clinicName: 'Dê um nome à clínica, de 2 a 80 caracteres.',
  codeLength: 'O código tem 6 dígitos. Confira o e-mail e digite todos.',
  invalid_code: 'O código não confere ou venceu. Peça outro.',
  rate_limited: 'Muitas tentativas em pouco tempo. Aguarde um minuto e tente de novo.',
  refused: 'Não foi possível entrar agora. Tente de novo em instantes.',
} as const

type Values = Record<FieldKey, string>
const EMPTY: Values = { name: '', email: '', password: '', clinicName: '', docNumber: '' }

function validate(key: FieldKey, values: Values): string | null {
  switch (key) {
    case 'name':
      return personNameError(values.name) ?? null
    case 'email':
      return isEmail(values.email) ? null : MESSAGES.email
    case 'password':
      return isAcceptablePassword(values.password) ? null : MESSAGES.password
    case 'clinicName': {
      const length = values.clinicName.trim().length
      return length >= 2 && length <= 80 ? null : MESSAGES.clinicName
    }
    case 'docNumber':
      return docNumberError(values.docNumber) ?? null
  }
}

export function SignupPage() {
  const session = useSession()
  const navigate = useNavigate()
  const signup = useSignup()
  const resend = useRequestCode()
  const ids = {
    name: useId(),
    email: useId(),
    password: useId(),
    clinicName: useId(),
    docNumber: useId(),
    code: useId(),
  }
  const [step, setStep] = useState<Step>('account')
  const [values, setValues] = useState<Values>(EMPTY)
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({})
  // O que a API recusou vale até a pessoa mexer no campo.
  const [refused, setRefused] = useState<Partial<Record<FieldKey, string>>>({})
  const [showPassword, setShowPassword] = useState(false)
  // O cadastro criado: para qual e-mail o código foi e quando (libera o reenvio).
  const [sent, setSent] = useState<{ email: string; minutes: number; at: number } | null>(null)
  const [code, setCode] = useState('')
  const [codeTouched, setCodeTouched] = useState(false)
  const [codeRefused, setCodeRefused] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [verifying, setVerifying] = useState(false)
  const secondsToResend = useSecondsUntil(sent ? sent.at + RESEND_AFTER_SECONDS * 1000 : null)

  // Quem já entrou não tem o que criar aqui. Durante a conferência quem decide o destino é a tela.
  if (!verifying && session.status === 'signed_in') return <Navigate to="/" replace />
  if (!verifying && session.status === 'password_required') {
    return <Navigate to={routes.newPassword} replace />
  }

  const errorOf = (key: FieldKey) => refused[key] ?? (touched[key] ? validate(key, values) : null)

  function change(key: FieldKey, text: string) {
    setValues((current) => ({ ...current, [key]: text }))
    setRefused((current) => (current[key] ? { ...current, [key]: undefined } : current))
  }

  /** Marca os campos do passo como tocados e diz se todos passam. */
  function checkAll(keys: FieldKey[]) {
    setTouched((current) => ({ ...current, ...Object.fromEntries(keys.map((k) => [k, true])) }))
    return keys.every((key) => validate(key, values) === null)
  }

  function submitAccount(event: FormEvent) {
    event.preventDefault()
    if (!checkAll(ACCOUNT_FIELDS)) return
    setStep('clinic')
  }

  async function submitClinic(event: FormEvent) {
    event.preventDefault()
    if (!checkAll(CLINIC_FIELDS) || signup.isPending) return
    const email = values.email.trim().toLowerCase()
    try {
      const started = await signup.mutateAsync({
        name: values.name.trim(),
        email,
        password: values.password,
        clinic: {
          name: values.clinicName.trim(),
          docNumber: normalizeDocNumber(values.docNumber),
        },
      })
      setSent({ email: started.email, minutes: started.expiresInMinutes, at: Date.now() })
      setStep('confirm')
    } catch (error) {
      const fields = fieldErrors(error)
      const placed: Partial<Record<FieldKey, string>> = {}
      for (const [path, message] of Object.entries(fields)) {
        const key = API_FIELDS[path]
        if (key && !placed[key]) placed[key] = message
      }
      setRefused(placed)
      // Um erro num campo do primeiro passo manda a pessoa de volta a ele.
      if (ACCOUNT_FIELDS.some((key) => placed[key])) setStep('account')
    }
  }

  async function requestAnotherCode() {
    if (!sent || resend.isPending) return
    setRefusal(null)
    try {
      await resend.mutateAsync(sent.email)
      setSent({ ...sent, at: Date.now() })
      setCode('')
      setCodeTouched(false)
      setCodeRefused(null)
    } catch {
      // A causa fica em `resend.error`, mostrada abaixo do campo.
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault()
    setCodeTouched(true)
    if (!sent || code.length !== 6 || verifying) return
    setVerifying(true)
    setCodeRefused(null)
    setRefusal(null)
    const result = await session.verifyCode(sent.email, code)
    if (result.error) {
      setVerifying(false)
      if (result.error.kind === 'invalid_code') setCodeRefused(MESSAGES.invalid_code)
      else setRefusal(result.error.message ?? MESSAGES[result.error.kind])
      return
    }
    // E-mail confirmado e sessão aberta: a porta de entrada leva à implantação (RN-ADM-80).
    navigate('/', { replace: true })
  }

  const progress = (
    <StepProgress
      className="mt-6"
      current={STEPS.indexOf(step) + 1}
      total={STEPS.length}
      label={STEP_LABELS[step]}
    />
  )

  // O 409 diz o caminho: a conta já existe (entrar) ou parou antes do código (entrar com código).
  const signupRefusal =
    signup.isError && Object.keys(fieldErrors(signup.error)).length === 0 ? signup.error : null
  const conflictAction: ReactNode =
    signupRefusal && isApiError(signupRefusal) && signupRefusal.status === 409 ? (
      isPendingVerification(signupRefusal) ? (
        <Button variant="outline" size="sm" asChild>
          <Link to={routes.signInCode} state={{ email: values.email.trim() }}>
            Entrar com código
          </Link>
        </Button>
      ) : (
        <Button variant="outline" size="sm" asChild>
          <Link to={routes.login}>Entrar</Link>
        </Button>
      )
    ) : undefined

  const field = (
    key: FieldKey,
    label: string,
    input: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'id' | 'value' | 'onChange'> & {
      mask?: (text: string) => string
    } = {},
    hint?: string,
  ) => {
    const { mask, ...rest } = input
    const error = errorOf(key)
    const noteId = `${ids[key]}-note`
    return (
      <>
        <label htmlFor={ids[key]} className="eyebrow mt-6">
          {label}
        </label>
        <input
          id={ids[key]}
          value={values[key]}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || hint ? noteId : undefined}
          onChange={(event) => change(key, mask ? mask(event.target.value) : event.target.value)}
          onBlur={() => setTouched((current) => ({ ...current, [key]: true }))}
          className={authInput}
          {...rest}
        />
        <FieldNote id={noteId} error={error} hint={hint} />
      </>
    )
  }

  if (step === 'account') {
    return (
      <AuthLayout eyebrow="NOVA CLÍNICA" title={STEP_TITLES.account} onSubmit={submitAccount}>
        {progress}
        <p className={cn(authNote, 'mt-4')}>
          Você será a gestora da clínica: cadastra a equipe, os procedimentos e os documentos.
        </p>
        {field('name', 'Nome completo', { type: 'text', autoComplete: 'name' })}
        {field('email', 'E-mail', { type: 'email', autoComplete: 'username', inputMode: 'email' })}
        <PasswordLabel
          htmlFor={ids.password}
          shown={showPassword}
          onToggle={() => setShowPassword((value) => !value)}
          className="mt-6"
        >
          Senha
        </PasswordLabel>
        <input
          id={ids.password}
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          value={values.password}
          aria-invalid={errorOf('password') ? true : undefined}
          aria-describedby={`${ids.password}-note`}
          onChange={(event) => change('password', event.target.value)}
          onBlur={() => setTouched((current) => ({ ...current, password: true }))}
          className={authInput}
        />
        <FieldNote
          id={`${ids.password}-note`}
          error={errorOf('password')}
          hint="Pelo menos 8 caracteres, com letras e números."
        />
        <Button type="submit" size="lg" className="mt-7">
          Continuar
        </Button>
        <p className={cn(authNote, 'mt-4')}>
          Já tem conta?{' '}
          <Link to={routes.login} className={authInlineLink}>
            Entrar
          </Link>
        </p>
      </AuthLayout>
    )
  }

  if (step === 'clinic') {
    return (
      <AuthLayout eyebrow="NOVA CLÍNICA" title={STEP_TITLES.clinic} onSubmit={submitClinic}>
        {progress}
        <p className={cn(authNote, 'mt-4')}>
          O nome e o documento entram em todo documento emitido. Endereço, responsável legal e
          testemunhas você completa depois, na implantação.
        </p>
        {field('clinicName', 'Nome da clínica', { type: 'text', autoComplete: 'organization' })}
        {field(
          'docNumber',
          'CNPJ ou CPF',
          { type: 'text', inputMode: 'numeric', mask: (text) => formatDocNumber(text) },
          'O CPF, se a clínica é pessoa física.',
        )}

        {signupRefusal ? (
          <ActionError
            className="mt-6"
            title="Não foi possível criar a conta"
            description={errorMessage(signupRefusal)}
            actions={conflictAction}
          />
        ) : null}

        <Button
          type="submit"
          size="lg"
          className="mt-7"
          loading={signup.isPending}
          loadingLabel="Criando a conta…"
        >
          Criar conta
        </Button>
        <div className="mt-5 flex">
          <button type="button" onClick={() => setStep('account')} className={authLink}>
            Voltar
          </button>
        </div>
        <WaitPhrase moment="saving" active={signup.isPending} className="mt-4" />
      </AuthLayout>
    )
  }

  const codeError = codeRefused ?? (codeTouched && code.length !== 6 ? MESSAGES.codeLength : null)
  const resendError = resend.isError ? errorMessage(resend.error) : null

  return (
    <AuthLayout eyebrow="NOVA CLÍNICA" title={STEP_TITLES.confirm} onSubmit={submitCode}>
      {progress}
      <p className={cn(authNote, 'mt-4')}>
        Enviamos um código de 6 dígitos para{' '}
        <span className="text-ink break-words">{sent?.email}</span>. Ele vale por{' '}
        {sent?.minutes ?? 10} minutos e só funciona uma vez.
      </p>

      <label htmlFor={ids.code} className="eyebrow mt-8">
        Código
      </label>
      <input
        id={ids.code}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        value={code}
        aria-invalid={codeError ? true : undefined}
        aria-describedby={codeError ? `${ids.code}-error` : undefined}
        onChange={(event) => {
          // Sem `maxLength`: o e-mail mostra "482 913", e o navegador cortaria o último dígito ao
          // colar. O corte vem depois de tirar o que não é dígito.
          setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
          setCodeRefused(null)
        }}
        onBlur={() => setCodeTouched(true)}
        className={cn(authInput, 'font-display text-[18px] tracking-[.3em]')}
      />
      <FieldNote id={`${ids.code}-error`} error={codeError} />

      {refusal ? (
        <ActionError className="mt-6" title="Não foi possível entrar" description={refusal} />
      ) : null}
      {resendError ? (
        <ActionError
          className="mt-6"
          title="Não foi possível enviar outro código"
          description={resendError}
        />
      ) : null}

      <Button
        type="submit"
        size="lg"
        className="mt-7"
        loading={verifying}
        loadingLabel="Confirmando…"
      >
        Confirmar e entrar
      </Button>
      <Button
        variant="outline"
        size="lg"
        className="mt-3"
        disabled={secondsToResend > 0}
        loading={resend.isPending}
        loadingLabel="Enviando…"
        onClick={() => void requestAnotherCode()}
      >
        {secondsToResend > 0
          ? `Enviar outro código em ${secondsToResend} s`
          : 'Enviar outro código'}
      </Button>
      <p className={cn(authNote, 'mt-5')}>
        Fechou a página antes de confirmar? Depois, entre com código pelo mesmo e-mail: ele confirma
        o endereço e continua de onde parou.
      </p>
      <WaitPhrase moment="signing_in" active={verifying} className="mt-4" />
    </AuthLayout>
  )
}
